import { ref, reactive, onActivated, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete } from '@element-plus/icons-vue'
import { request } from '@/util/globalContext'
import { enumSelect, listCommon } from '@/util/vueutil/commonList.js' // 引入通用方法

export default function useBillList() {
    // 响应式数据定义
    const listRef = ref(null)
    const dialogVisible = ref(false)
    const dialogType = ref('add')
    const formRef = ref(null)
    const Options = ref({ //初始化枚举
        isDeleted: [],
        cst_type: []
    });

    // 搜索表单
    const searchForm = reactive({
        cstPhone: '',        // 手机号
        cstName: '',         // 客户名称
        cstType: '',         // 客户性质
    })

    // 引入通用方法
    const {
        oneShow,
        listShow,
        handleReset,
        handleSelectionChange,
        handleCurrentRowChange
    } = listCommon(searchForm)

    // 表单默认值
    const FORM_DEFAULT = {
        cstId: '',              // 主键（编辑用）
        cstPhone: '',        // 手机号
        cstName: '',         // 客户名称
        cstType: '',         // 客户性质
        isDeleted: '0',     // 是否生效
        cstAreaCodes: '',    // 省市区编码，逗号分隔
        cstAreaNames: '',    // 省市区名称，逗号分隔
        cstAddressDetail: '',// 详细地址
        remark: ''           // 备注
    }
    const formData = reactive({ ...FORM_DEFAULT })
    const areaValue = ref([]) // 省市区选中值
    const fullAreaTree = ref([]) // 完整的地址树缓存

    // 从接口获取完整地址树，页面周期内缓存
    const loadAreaData = async (forceRefresh = false) => {
        if (!forceRefresh && fullAreaTree.value && fullAreaTree.value.length > 0) {
            return
        }
        
        try {
            const res = await request.get('/area/getAreaTree')

            if (res.code === 200 && res.data) {
                fullAreaTree.value = res.data
            }
        } catch (error) {
            console.error('加载地址数据失败', error)
        }
    }
    
    // 根据省市区编码数组获取对应的名称数组
    const getAreaNamesFromCodes = (areaCodes) => {
        if (!areaCodes || !areaCodes.length) return []
        
        const names = []
        let currentLevel = fullAreaTree.value
        
        for (const code of areaCodes) {
            const found = currentLevel.find(item => item.value === code)
            if (found) {
                names.push(found.label)
                currentLevel = found.children || []
            }
        }
        
        return names
    }

    // 表单校验规则
    const formRules = reactive({
        cstPhone: [
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
        ],
        cstName: [
            { required: true, message: '请输入客户名称', trigger: 'blur' },
            { max: 100, message: '客户名称最多100个字符', trigger: 'blur' }
        ],
        cstType: [
            { required: true, message: '请选择客户性质', trigger: 'change' }
        ],
        isDeleted: [
            { required: true, message: '请选择是否生效', trigger: 'change' }
        ],
        cstAddress: [
            { max: 250, message: '客户地址最多250个字符', trigger: 'blur' }
        ],
        remark: [
            { max: 250, message: '备注最多250个字符', trigger: 'blur' }
        ]
    })

    // 加载列表
    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)
        try {
            const params = {
                ...paginationParams,
                cstPhone: searchForm.cstPhone,
                cstName: searchForm.cstName,
                cstType: searchForm.cstType
            }
            const res = await request.get('/cstmgt/getCstList', { params })
            if (res.code === 200) {
                listRef.value.updateListData(res.data.records || [])
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 新增
    const handleAdd = async () => {
        // 确保地址数据已加载（强制刷新）
        await loadAreaData(true)
        dialogType.value = 'add'
        formRef.value?.resetFields()
        // 重置表单
        Object.assign(formData, JSON.parse(JSON.stringify(FORM_DEFAULT)))
        // 重置省市区
        areaValue.value = []
        // 表单显示
        dialogVisible.value = true
    }
    
    // 获取完整的地址显示字符串
    const getFullAddressDisplay = (row) => {
        if (row.cstAreaNames) {
            const areaNames = row.cstAreaNames.split(',').filter(Boolean)
            const areaStr = areaNames.join('')
            const detailStr = row.cstAddressDetail || ''
            return areaStr + (areaStr && detailStr ? ' ' : '') + detailStr
        }
        return ''
    }

    // 编辑
    const handleEdit = async () => {
        if (!listShow.value) { // 替换为新命名 listShow
            ElMessage.warning('请先选中一条数据！')
            return
        }
        if (!listRef.value?.isInited) {
            ElMessage.warning('列表组件初始化中，请稍后重试')
            return
        }

        try {
            // 确保地址数据已加载（强制刷新）
            await loadAreaData(true)
            listRef.value.setLoading(true)
            const params = {
                cstId: listShow.value.cstId // 替换为新命名 listShow
            }
            const res = await request.get('/cstmgt/getCstList', { params })
            if (res.code !== 200) {
                ElMessage.error(res.msg || '接口异常')
                return
            }
            const cstData = res.data.records[0]
            // 赋值表单数据
            formData.id = cstData.id
            formData.cstId = cstData.cstId
            formData.cstPhone = cstData.cstPhone
            formData.cstName = cstData.cstName
            formData.cstType = cstData.cstType
            formData.isDeleted = cstData.isDeleted
            formData.remark = cstData.remark
            
            // 读取地址数据
            if (cstData.cstAreaCodes) {
                areaValue.value = cstData.cstAreaCodes.split(',').filter(Boolean)
            } else {
                areaValue.value = []
            }
            formData.cstAddressDetail = cstData.cstAddressDetail || ''
            formData.cstAreaCodes = cstData.cstAreaCodes
            formData.cstAreaNames = cstData.cstAreaNames

            dialogType.value = 'edit'
            dialogVisible.value = true
        } catch (error) {
            ElMessage.error('获取详情失败：' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 删除
    const handleDelete = async () => {
        if (!listShow.value) { // 替换为新命名 oneShow
            ElMessage.warning('请先选中数据！')
            return
        }
        try {
            const isValidCst = listShow.value.isDeleted === '0';
            let confirmMessage = '确定删除选中的客户数据？';
            if (isValidCst) {
                confirmMessage = `${listShow.value.cstName}为有效客户，请谨慎删除！<br/>确定要继续删除吗？`;
            }
            await ElMessageBox.confirm(
                confirmMessage,
                '删除警告',
                { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning', dangerouslyUseHTMLString: true }
            );

            listRef.value.setLoading(true)
            const params = {
                id: listShow.value.id
            }
            const res = await request.post('/cstmgt/deleteCst', params)
            if (res.code === 200) {
                ElMessage.success('删除成功')
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                ElMessage.error('删除失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            if (error !== 'cancel') {
                ElMessage.error('删除失败：网络异常')
            }
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 提交 表单
    const handleSubmit = async () => {
        if (!formRef.value) return
        const isValid = await new Promise((resolve) => {
            formRef.value.validate((valid) => {
                resolve(valid);
            });
        });
        if (!isValid) return;

        // 将省市区数组转换为编码字符串和名称字符串
        const areaCodes = areaValue.value.join(',')
        const areaNames = getAreaNamesFromCodes(areaValue.value).join(',')
        
        const submitData = {
            cstId: formData.cstId,
            cstPhone: formData.cstPhone,
            cstName: formData.cstName,
            cstType: formData.cstType,
            isDeleted: formData.isDeleted,
            cstAreaCodes: areaCodes,
            cstAreaNames: areaNames,
            cstAddressDetail: formData.cstAddressDetail || '',
            remark: formData.remark
        };

        listRef.value.setLoading(true)
        try {
            const res = await request.post('/cstmgt/saveOrUpdate', submitData)
            if (res.code === 200) {
                ElMessage.success(dialogType.value === 'add' ? '新增成功' : '修改成功')
                dialogVisible.value = false
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            } else {
                const tip = dialogType.value === 'add' ? '新增失败' : '修改失败'
                ElMessage.error(tip + '：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('接口异常：' + error)
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // 生命周期 仅在页面初次打开时
    onMounted(async () => {
        console.log('=== onMounted 开始执行 ===')
        await nextTick();
        // 页面加载时加载枚举和地址数据
        console.log('开始加载枚举和地址数据')
        await Promise.all([
            enumSelect('enumType', 'isDeleted').then(list => {
                console.log('isDeleted 枚举加载完成', list)
                Options.value.isDeleted = list
            }).catch(() => []),
            enumSelect('enumType', 'cst_type').then(list => {
                console.log('cst_type 枚举加载完成', list)
                Options.value.cst_type = list
            }).catch(() => []),
            loadAreaData()
        ]);
        // 加载列表
        setTimeout(() => {
            console.log('开始加载列表')
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        }, 50)
        console.log('=== onMounted 执行完毕 ===')
    })

    //页面切回时 需要配合 vue缓存 先不管他
    onActivated(() => {
        // 激活时：复用缓存，无需重复请求，仅兜底赋值（防止缓存丢失）
        Promise.all([
            enumSelect('enumType', 'isDeleted').then(list => Options.value.isDeleted = list).catch(() => []),
            enumSelect('enumType', 'cst_type').then(list => Options.value.cst_type = list).catch(() => []),
            loadAreaData()
        ]).then(() => {
            // 加载列表
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
        })
    })
    // 暴露变量/方法
    return {
        searchForm,
        listRef,
        dialogVisible,
        dialogType,
        formRef,
        oneShow,
        listShow,
        formData,
        formRules,
        Plus, Edit, Delete,
        handleSelectionChange,
        handleCurrentRowChange,
        handleAdd,
        handleEdit,
        handleDelete,
        handleSubmit,
        handleLoadList,
        handleReset,
        Options,
        areaValue,
        fullAreaTree,
        getFullAddressDisplay
    }
}